import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./SmartScrollRow.scss";

/**
 * Horizontal row with overflow-x: auto. Prev/next shown only when content is wider than the viewport.
 */
const SmartScrollRow = ({
  children,
  className = "",
  trackClassName = "",
  rtl = true,
  edgeSize = 36,
  shrinkToContent = false,
}) => {
  const trackRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth + 2);
  }, []);

  useLayoutEffect(() => {
    let frameId = 0;

    const scheduleMeasure = () => {
      updateOverflow();
      frameId = requestAnimationFrame(updateOverflow);
    };

    scheduleMeasure();
    const el = trackRef.current;
    if (!el) return () => cancelAnimationFrame(frameId);

    const ro = new ResizeObserver(() => scheduleMeasure());
    ro.observe(el);
    if (el.parentElement) {
      ro.observe(el.parentElement);
    }
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      cancelAnimationFrame(frameId);
      ro.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [updateOverflow, children]);

  const scroll = (forward) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.min(300, Math.floor(el.clientWidth * 0.72));
    const isRtl = getComputedStyle(el).direction === "rtl";
    const delta = isRtl
      ? forward
        ? -amount
        : amount
      : forward
        ? amount
        : -amount;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div
      className={`smart-scroll-row${hasOverflow ? " smart-scroll-row--overflow" : ""}${shrinkToContent ? " smart-scroll-row--shrink" : ""}${className ? ` ${className}` : ""}`.trim()}
    >
      {hasOverflow && (
        <button
          type="button"
          className="smart-scroll-row__edge smart-scroll-row__edge--start"
          style={{ width: edgeSize, minWidth: edgeSize }}
          onClick={() => scroll(false)}
          aria-label="גרור לכיוון התחלה"
        >
          <ChevronRight
            className="smart-scroll-row__icon"
            size={20}
            strokeWidth={2}
            aria-hidden
          />
        </button>
      )}
      <div
        ref={trackRef}
        className={`smart-scroll-row__track${trackClassName ? ` ${trackClassName}` : ""}`.trim()}
        dir={rtl ? "rtl" : "ltr"}
        onScroll={updateOverflow}
      >
        {children}
      </div>
      {hasOverflow && (
        <button
          type="button"
          className="smart-scroll-row__edge smart-scroll-row__edge--end"
          style={{ width: edgeSize, minWidth: edgeSize }}
          onClick={() => scroll(true)}
          aria-label="גרור לכיוון הסוף"
        >
          <ChevronLeft
            className="smart-scroll-row__icon"
            size={20}
            strokeWidth={2}
            aria-hidden
          />
        </button>
      )}
    </div>
  );
};

export default SmartScrollRow;
